import authHeader from "@/services/auth/AuthHeader";
import axios from "axios";
import MathUtil from "@/util/MathUtil";

class VivaService {
    /**
     * Convert amount to cents (Viva expects amounts in minor units)
     * Example: 199.99 -> 19999
     */
    createAmountForViva(amount){
        let amountForViva = MathUtil.displayTwoDecimals(amount).replace(',', ''); // Remove comma
        return parseInt(amountForViva);
    }

    /**
     * Refund a Viva payment
     *
     * @param transactionId - The Viva transaction ID
     * @param amount - The refund amount (will be converted to cents)
     * @param cartItems - The cart items being refunded
     * @param refundedTipsAmount - Tips amount being refunded
     * @param orderDocumentId - The order document ID in Couchbase
     */
    refund(transactionId, amount, cartItems, refundedTipsAmount, orderDocumentId, refundedBonusAmount){
        let user = JSON.parse(localStorage.getItem('user'));
        let url = '/api/payment/vivaRefund';
        let basicAuth = authHeader();
        return axios.post(url,
            {
                merchant: user.merchant,
                transactionId: transactionId,
                amount: this.createAmountForViva(amount),
                cartItems: cartItems,
                refundedTipsAmount: refundedTipsAmount,
                orderDocumentId: orderDocumentId,
                refundedBonusAmount: refundedBonusAmount || 0
            },
            {headers: basicAuth });
    }

    /**
     * SUPER_ADMIN refunds a till or kiosk VIVA payment from Backoffice — no card needed, VIVA reverses it by
     * transaction id. A separate endpoint from refund() above because the server then does what the till
     * does after its own refund: adds it to the till's grand total of returns and writes the return in the
     * electronic journal. Which orders qualify is decided there (VIVA card only, never cash, not yet in a
     * Z-report); the merchant is read from the order, not from here.
     */
    terminalRefund(transactionId, amount, cartItems, refundedTipsAmount, orderDocumentId){
        let user = JSON.parse(localStorage.getItem('user'));
        return axios.post('/api/superadmin/payment/vivaTerminalRefund',
            {
                merchant: user.merchant,
                transactionId: transactionId,
                amount: this.createAmountForViva(amount),
                cartItems: cartItems,
                refundedTipsAmount: refundedTipsAmount,
                orderDocumentId: orderDocumentId
            },
            {headers: authHeader() });
    }

    /**
     * What a failed refund tells the person at the till. /vivaRefund answers SUCCEEDED, or FAILED, or a
     * cause it can name. NO_VIVA_ACCOUNT: the location takes VIVA payments (the till's terminal app needs
     * no viva_account) but was never connected as a VIVA account in CAGISTA, so a refund has no account
     * to go through. Nobody at the till can fix that — they are told so, and to call us, instead of a
     * generic error they would retry. Worded for the card part only: in a cash + card refund the cash may
     * already have been paid out.
     */
    refundFailedMessage(responseData, amount){
        // INSUFFICIENT_FUNDS: VIVA pays a refund out of the restaurant's VIVA balance — a same-day cancellation
        // too — and counts a sale only once it has cleared. mollapizza, first day on VIVA (2026-10-12): refused
        // four times, through at 14:44; next morning, with that day's sales cleared, a same-day cancellation went
        // straight through. Nothing is wrong and nobody needs calling — say when it will work, and that it has to
        // be before the Z-report, after which Backoffice no longer offers the refund.
        if(responseData === 'INSUFFICIENT_FUNDS'){
            const what = amount != null && !isNaN(amount)
                ? 'kr ' + Number(amount).toFixed(2).replace('.', ',')
                : 'beløpet';
            // The last sentence is the advice, here because this is where they learn it — and the one place a
            // restaurant without SaaS features sees it, since Aktiver VIVA-betaling is hidden from them.
            return 'Viva-kontoen har ikke nok saldo til å betale tilbake ' + what + '. Viva trekker refusjoner fra '
                + 'saldoen på kontoen — også når betalingen kanselleres samme dag — og et salg teller først når det '
                + 'er oppgjort. Beløpet er ikke tilbakeført til kortet. Prøv igjen litt senere, før dagsoppgjøret. '
                + 'La det stå igjen litt på Viva-kontoen når dere overfører til banken, så kan dere alltid '
                + 'refundere med en gang.';
        }
        if(responseData === 'NO_VIVA_ACCOUNT'){
            return 'Viva-kontoen til denne avdelingen er ikke koblet til CAGISTA, så refusjonen kunne ikke '
                + 'sendes til Viva. Beløpet er ikke tilbakeført til kortet. Kontakt CAGISTA support.';
        }
        // NOT_ELIGIBLE: VIVA will not let CAGISTA reverse this payment — it was taken outside CAGISTA's reseller
        // link (kiosk payments before TastyKIOSK sent the ISV parameters). The merchant can still refund it in
        // VIVA, so say where, rather than send them to support for something they can do themselves.
        if(responseData === 'NOT_ELIGIBLE'){
            return 'Viva tillater ikke at denne betalingen refunderes fra CAGISTA. Beløpet er ikke tilbakeført '
                + 'til kortet. Refunder den i Viva (på viva.com eller i Viva-appen).';
        }
        // The Backoffice refund of a till or kiosk payment refuses these before anything is sent to VIVA.
        const terminalRefusals = {
            SETTLED: 'Ordren er tatt med i dagsoppgjøret og kan ikke lenger refunderes.',
            NOT_CARD_ONLY: 'Ordren er ikke betalt bare med Viva-kort. Kontant må refunderes i kassen.',
            ALREADY_REFUNDED: 'Ordren er allerede refundert.',
            AMOUNT_NOT_REFUNDABLE: 'Beløpet er mer enn det som er igjen å refundere av denne betalingen. '
                + 'Last siden på nytt — den kan være refundert fra en annen fane eller i kassen.',
            NO_SUCH_PAYMENT: 'Fant ikke Viva-betalingen på denne ordren.',
            NOT_A_TERMINAL_ORDER: 'Dette er ikke en kasse- eller kioskordre.'
        };
        if(terminalRefusals[responseData]){
            return terminalRefusals[responseData] + ' Ingenting er sendt til Viva.';
        }
        return 'Det skjedde en feil og refusjonen ble ikke fullført.';
    }
}

export default new VivaService();
